import type { MarketPage, MarketRecord } from '../types'

/**
 * Filling one page of market data, and making sure only the latest search
 * gets to show it.
 *
 * Kept apart from the screen so the offline suite can run it against the real
 * search function, at the size of a real survey. No test here mounts screens,
 * and the faults this exists for are about order and timing, which a check on
 * the screen's source cannot see.
 */

/**
 * Records a page.
 *
 * Enough to scroll through and get a feel for a survey, short enough that a
 * search over a large one answers quickly. More arrives on request rather
 * than all at once, because a survey can hold a hundred thousand rows and
 * nobody wants them in one page.
 */
export const PAGE = 40

/**
 * How many more reads one page may take before it stops and asks.
 *
 * Each read can spend eight seconds on the server, so twelve is about a
 * minute and a half. Past that the screen says the page is still short and
 * offers to keep going, rather than leaving somebody watching a spinner with
 * no end to it.
 */
export const MORE_READS = 12

/** Asks the table for up to `want` matches, from a cursor or from a line. */
export type Ask = (
  start: { cursor: string | null; from?: number },
  want: number,
  signal: AbortSignal,
) => Promise<MarketPage>

/** One search, and whether it is still the one the screen is waiting for. */
export interface Turn {
  readonly signal: AbortSignal
  current(): boolean
}

/**
 * Hands out turns, where starting one stops the one before.
 *
 * **This is what keeps a half-typed word off the screen.** Every keystroke
 * started a search, nothing stopped the earlier ones, and each could keep
 * reading for a minute and a half. One for the first two letters of a job
 * code finished long after the one for the whole code and put its own answer
 * on screen: the right rows appeared, then "Nothing matched" replaced them.
 * The overlapping searches also read the same part of the table at once, and
 * the table refused some of them, which showed as "That did not load".
 */
export function latestOnly(): { begin(): Turn; end(): void } {
  let latest = 0
  let stop: AbortController | null = null
  return {
    begin() {
      stop?.abort()
      const mine = ++latest
      const own = new AbortController()
      stop = own
      return { signal: own.signal, current: () => latest === mine }
    },
    // Nothing running counts any more, as when the screen is left.
    end() {
      stop?.abort()
      stop = null
      latest++
    },
  }
}

/** Thrown where a search finds it has been replaced. Never shown to anybody. */
export class Replaced extends Error {
  constructor() {
    super('Replaced by a later search.')
    this.name = 'Replaced'
  }
}

const stillMine = (turn: Turn) => {
  if (!turn.current()) throw new Replaced()
}

/** What a filled page came to. */
export interface Filled {
  page: MarketPage
  /** Stopped after the most reads one page may take, still short with more to read. */
  paused: boolean
}

/**
 * Reads until the page is full or nothing is left.
 *
 * The server answers within a time budget, and when the table is busy it
 * answers with what it found before the table refused it. Either way a page
 * can come back short with more of the table behind it. Shown as it arrives,
 * a short page reads as the whole answer: one match of a dozen, and page
 * numbers that offer a second page of nothing.
 *
 * So a short page with a cursor is read on from that cursor, asking only for
 * the rows still missing, and what comes back joins the same page. The page
 * after it then starts exactly where this one ends.
 *
 * `have` is a page already partly filled, for carrying on after a pause.
 * `onSome` is told what has been found so far, before each further read.
 */
export async function readPage(
  ask: Ask,
  start: { cursor: string | null; from?: number },
  turn: Turn,
  onSome?: (soFar: MarketPage) => void,
  have: MarketRecord[] = [],
): Promise<Filled> {
  stillMine(turn)
  let got = await ask(start, PAGE - have.length, turn.signal)
  stillMine(turn)
  let items = [...have, ...got.items]
  for (let more = 0; items.length < PAGE && got.cursor; more++) {
    if (more >= MORE_READS) return { page: { ...got, items, shown: items.length }, paused: true }
    onSome?.({ ...got, items, shown: items.length })
    got = await ask({ cursor: got.cursor }, PAGE - items.length, turn.signal)
    stillMine(turn)
    items = [...items, ...got.items]
  }
  return { page: { ...got, items, shown: items.length }, paused: false }
}

/**
 * Whether a page after this one can be offered.
 *
 * Only a full page with more behind it. A short page that still had a cursor
 * offered a second page, so a search finding one record showed "1 2".
 */
export const nextPageOpen = (page: MarketPage | null): page is MarketPage & { cursor: string } =>
  Boolean(page && page.items.length >= PAGE && page.cursor)

/**
 * Runs one search and hands its answer on only while it is still the latest.
 *
 * A search that was replaced says nothing at all: no rows, no error and no
 * end to the busy state, because the search that replaced it owns all three.
 */
export async function lastWins<T>(
  turn: Turn,
  work: () => Promise<T>,
  on: { done: (value: T) => void; failed: (message: string) => void; settled: () => void },
): Promise<void> {
  try {
    const value = await work()
    if (turn.current()) on.done(value)
  } catch (err) {
    if (turn.current() && !(err instanceof Replaced) && (err as Error)?.name !== 'AbortError') {
      on.failed((err as Error).message)
    }
  } finally {
    if (turn.current()) on.settled()
  }
}
